import { Alert, Button, Card, message, Space } from "antd";
import type { Page, ShareOperationRecordDetail, ShareStatisticsVo } from "@zhihaoscm/entity";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import {
    BaseItem as ZhBaseItem,
    DateRangePicker as ZhDateRangePicker,
    Dialog as ZhDialog,
    Grid as ZhGrid,
    MultiGrid as ZhMultiGrid,
    Pagination as ZhPagination,
    Select as ZhSelect,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";

import { DEFAULT_VALUE, LOG_DATE_FORMAT_END, LOG_DATE_FORMAT_START } from "@/config";
import { useUserStore } from "@/stores/react/authStore";
import { dateFormat } from "@/utils";
import { formatNumber } from "@/utils/number";
import { SHIP_WAY_BILL_TYPE } from "@/meta/logistics/waybill";
import { FREIGHT_RATE_TYPE } from "@/meta/logistics/shipNeed";
import {
    FIND_SHIP_TYPE,
    TRANSPORT_ORDER_SHIP_QUERY_TYPE,
    SHARE_OPERATION_RECORD_CLIENT_TYPE,
    SHARE_OPERATION_RECORD_OPERATION_ENTRY,
    SHARE_OPERATION_RECORD_OPERATOR_CARRIER,
    SHARE_OPERATION_RECORD_OPERATOR_METHOD,
    SHARE_OPERATION_RECORD_OPERATOR_ROLE,
    YES_OR_NO
} from "@zhihaoscm/meta";
import { JUMP_PATH, TABS } from "./utils";

type ShareDetail = Omit<Partial<ShareStatisticsVo>, "share" | "requirementVo" | "emptyShipVo"> & {
    share?: Partial<ShareStatisticsVo["share"]>;
    requirementVo?: Partial<ShareStatisticsVo["requirementVo"]>;
    emptyShipVo?: Partial<ShareStatisticsVo["emptyShipVo"]>;
};

export interface ShareStatisticalDetailQuery {
    page: number;
    size: number;
    trackId: string;
    businessType: 1 | 2;
    sortKey?: string;
    sortOrder?: "asc" | "desc";
    beginTime?: string;
    endTime?: string;
    jumpPath?: number;
    operatorRole?: number;
}

export interface ShareStatisticalDetailExportQuery extends ShareStatisticalDetailQuery {
    /** Vue 详情导出接口要求导出总条数。 */
    size: number;
    /** Vue 需求/空船详情导出类型，当前均使用 plat=1。 */
    type: 1;
}

export interface ShareStatisticalDetailPageProps {
    trackId?: string;
    businessType?: 1 | 2;
    loadDetail?: (trackId: string, businessType: 1 | 2) => Promise<ShareDetail>;
    loadRows?: (query: ShareStatisticalDetailQuery) => Promise<Page<ShareOperationRecordDetail>>;
    exportRows?: (query: ShareStatisticalDetailExportQuery, filename: string) => Promise<unknown>;
    permissions?: string[];
}

/** 复用 Vue 分享统计详情接口，保持原 `/vo/{trackId}/{businessType}` 路径。 */
export async function loadShareStatisticalDetail(trackId: string, businessType: 1 | 2): Promise<ShareDetail> {
    const { shareStatisticsService } = await import("@/api/services/shareStatisticsService");
    return shareStatisticsService.getShareStatistics(trackId, businessType);
}

/** 复用 Vue 分享统计详情明细分页接口。 */
export async function loadShareStatisticalDetailRows(
    query: ShareStatisticalDetailQuery
): Promise<Page<ShareOperationRecordDetail>> {
    const { shareStatisticsService } = await import("@/api/services/shareStatisticsService");
    return shareStatisticsService.getShareStatisticsDetailsPaging(query) as unknown as Promise<
        Page<ShareOperationRecordDetail>
    >;
}

/** 复用 Vue 分享统计详情导出接口。 */
export async function exportShareStatisticalDetailRows(
    query: ShareStatisticalDetailExportQuery,
    filename: string
): Promise<unknown> {
    const { shareStatisticsService } = await import("@/api/services/shareStatisticsService");
    return shareStatisticsService.getShareStatisticsDetailsExport(query, filename);
}

const valueText = (value: unknown) => (value == null || value === "" ? DEFAULT_VALUE : String(value));

const dateText = (value: unknown) => (value ? dateFormat(String(value), "YYYY-MM-DD HH:mm") : DEFAULT_VALUE);

const metaText = (value: unknown, getIntro: (code: number) => string | null | undefined) => {
    if (value == null || value === "") return DEFAULT_VALUE;
    return getIntro(Number(value)) || DEFAULT_VALUE;
};

const customerText = (customer?: { institutionName?: string; realName?: string; mobile?: string }) =>
    valueText(customer?.institutionName || customer?.realName || customer?.mobile);

/** 对齐旧 Vue 空船详情的承运商脱敏与三段式展示规则。 */
const carrierText = (carrier?: { institutionName?: string; realName?: string; mobile?: string; code?: string }) => {
    if (!carrier) return DEFAULT_VALUE;
    if (carrier.institutionName && carrier.mobile && carrier.code) return DEFAULT_VALUE;
    return [
        carrier.institutionName || carrier.realName || DEFAULT_VALUE,
        carrier.mobile || DEFAULT_VALUE,
        carrier.code || carrier.institutionName || DEFAULT_VALUE
    ].join("/");
};

const yesNoText = (value: unknown) => metaText(value, code => YES_OR_NO.getIntroByCode(code));

type DetailFilterState = {
    operateTime?: [string, string] | null;
    jumpPath?: number;
    operatorRole?: number;
};

const selectOptions = (options: Array<{ label: string; value: number | string }>) =>
    options.map(option => ({ label: option.label, value: Number(option.value) }));

/** 将旧 Vue 详情搜索栏的本地值转换为详情分页接口参数。 */
const toDetailFilterQuery = (
    filters: DetailFilterState
): Pick<ShareStatisticalDetailQuery, "beginTime" | "endTime" | "jumpPath" | "operatorRole"> => {
    const query: Pick<ShareStatisticalDetailQuery, "beginTime" | "endTime" | "jumpPath" | "operatorRole"> = {};
    if (filters.jumpPath !== undefined && filters.jumpPath !== null) query.jumpPath = filters.jumpPath;
    if (filters.operatorRole !== undefined && filters.operatorRole !== null) query.operatorRole = filters.operatorRole;
    if (filters.operateTime?.[0] && filters.operateTime[1]) {
        query.beginTime = dateFormat(filters.operateTime[0], LOG_DATE_FORMAT_START);
        query.endTime = dateFormat(filters.operateTime[1], LOG_DATE_FORMAT_END);
    }
    return query;
};

const shipText = (ship?: { cnname?: string; name?: string; shipId?: string }) => {
    const values = [ship?.cnname, ship?.name, ship?.shipId].filter(Boolean);
    return values.length ? values.join("/") : DEFAULT_VALUE;
};

const summaryLink = (value: unknown, to: string, search?: string): ReactNode => {
    if (value == null || value === "") return DEFAULT_VALUE;
    return <Link to={{ pathname: to, search }}>{String(value)}</Link>;
};

/** 纯文本详情项保持标签和值在同一文本节点，兼容 zhihao BaseItem 的可访问辅助内容。 */
const renderDetailItem = (item: { label: string; value: ReactNode }) =>
    typeof item.value === "string" || typeof item.value === "number" ? (
        <div key={item.label}>
            {item.label}: {item.value}
        </div>
    ) : (
        <ZhBaseItem key={item.label} label={item.label} value={item.value} />
    );

/** 将旧 Vue 详情页的需求基本信息映射为 zhihao-ui-react BaseItem。 */
function getDemandSummary(detail?: ShareDetail): Array<{ label: string; value: ReactNode }> {
    const plat = detail?.requirementVo?.plat;
    return [
        {
            label: "编号",
            value: summaryLink(
                plat?.id,
                `/logistics/shippingNeed/platformShipNeedInfo/${plat?.id}`,
                "?from=shipNeedAnalysis"
            )
        },
        { label: "货主", value: customerText(detail?.requirementVo?.owener) },
        { label: "服务类型", value: metaText(plat?.serviceType, code => SHIP_WAY_BILL_TYPE.getIntroByCode(code)) },
        { label: "发布时间", value: dateText(detail?.requirementVo?.publishTime) },
        { label: "始发地", value: valueText(plat?.sourcePortName) },
        { label: "目的地", value: valueText(plat?.destinationPortName) },
        { label: "运货吨数", value: plat?.freightTons ? formatNumber(plat.freightTons) : DEFAULT_VALUE },
        { label: "船型", value: metaText(plat?.shipType, code => FIND_SHIP_TYPE.getIntroByCode(code) as string) },
        {
            label: "预期装货日期",
            value: plat?.loadDate ? `${dateFormat(plat.loadDate)} + ${valueText(plat.loadDays)}天` : DEFAULT_VALUE
        },
        {
            label: "意向运价",
            value:
                plat?.unitPriceType != null && FREIGHT_RATE_TYPE.CALL_FOR_PRICE.match(plat.unitPriceType)
                    ? FREIGHT_RATE_TYPE.CALL_FOR_PRICE.intro
                    : plat?.unitPrice != null
                      ? `${plat.unitPrice} 元/吨`
                      : DEFAULT_VALUE
        }
    ];
}

/** 将旧 Vue 详情页的空船基本信息映射为 zhihao-ui-react BaseItem。 */
function getEmptyShipSummary(detail?: ShareDetail): Array<{ label: string; value: ReactNode }> {
    const emptyShipVo = detail?.emptyShipVo;
    const emptyShip = emptyShipVo?.emptyShip;
    const shipId = emptyShipVo?.ship?.id;
    return [
        {
            label: "编号",
            value: summaryLink(emptyShip?.id, `/logistics/emptyShipInformationDetail/${emptyShip?.id}`)
        },
        {
            label: "船舶名称",
            value: shipId
                ? summaryLink(shipText(emptyShip), `/logistics/shipManage/shipInfo/shipInfoDetail/${shipId}`)
                : shipText(emptyShip)
        },
        { label: "承运商", value: carrierText(emptyShipVo?.carrier) },
        { label: "联系人", value: valueText(emptyShip?.captain) },
        { label: "联系方式", value: valueText(emptyShip?.mobile) },
        {
            label: "预期空船时间",
            value: emptyShip
                ? `${dateText(emptyShip.emptyStartTime)}至${dateText(emptyShip.emptyEndTime)}`
                : DEFAULT_VALUE
        },
        { label: "实装吨位", value: emptyShip?.tonCapacity ? formatNumber(emptyShip.tonCapacity) : DEFAULT_VALUE },
        {
            label: "空船始发地",
            value: emptyShipVo?.originPorts?.length
                ? emptyShipVo.originPorts.map(item => item.portName).join("、")
                : DEFAULT_VALUE
        },
        {
            label: "目的地",
            value: emptyShipVo?.destPorts?.length
                ? emptyShipVo.destPorts.map(item => item.portName).join("、")
                : DEFAULT_VALUE
        }
    ];
}

/** 分享统计详情首屏：沿用 Vue 详情标题、摘要网格、明细表格和分页契约。 */
export function ShareStatisticalDetailPage({
    trackId,
    businessType: businessTypeProp,
    loadDetail = loadShareStatisticalDetail,
    loadRows = loadShareStatisticalDetailRows,
    exportRows = exportShareStatisticalDetailRows,
    permissions
}: ShareStatisticalDetailPageProps) {
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    // 保持旧 Vue v-auth 语义：详情明细导出入口只对指定按钮权限开放。
    const canExport = (permissions ?? storePermissions).includes("ROLE_SHARE_STATISTICS_EXPORT");
    const routeParams = useParams<{ id?: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const resolvedTrackId = trackId ?? routeParams.id;
    const businessType = businessTypeProp ?? (searchParams.get("tab") === String(TABS.EMPTY_SHIP) ? 2 : 1);
    const [detail, setDetail] = useState<ShareDetail>();
    const [rows, setRows] = useState<ShareOperationRecordDetail[]>([]);
    const [page, setPage] = useState(1);
    // 保留 Vue zh-table 的草稿筛选与显式查询状态，避免输入过程中重复请求接口。
    const [filters, setFilters] = useState<DetailFilterState>({});
    const [submittedFilters, setSubmittedFilters] = useState<DetailFilterState>({});
    // 对齐旧 Vue zh-table 的 custom 排序参数，排序变化时重新请求当前详情明细。
    const [sort, setSort] = useState<{ key: string; order: "asc" | "desc" }>();
    const [total, setTotal] = useState(0);
    const [error, setError] = useState("");
    const [exportOpen, setExportOpen] = useState(false);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        if (!resolvedTrackId) return;
        let disposed = false;
        void loadDetail(resolvedTrackId, businessType)
            .then(result => {
                if (!disposed) setDetail(result);
            })
            .catch(() => {
                if (!disposed) setError("分享统计详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [businessType, loadDetail, resolvedTrackId]);

    useEffect(() => {
        if (!resolvedTrackId) return;
        let disposed = false;
        const query: ShareStatisticalDetailQuery = {
            page,
            size: 20,
            trackId: resolvedTrackId,
            businessType,
            ...toDetailFilterQuery(submittedFilters),
            ...(sort ? { sortKey: sort.key, sortOrder: sort.order } : {})
        };
        void loadRows(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
            })
            .catch(() => {
                if (!disposed) setError("分享统计明细加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [businessType, loadRows, page, resolvedTrackId, sort, submittedFilters]);

    const columns = useMemo<TableColumnProps<ShareOperationRecordDetail>[]>(() => {
        // 需求与空船明细共用访问基础字段，再按 Vue 业务页切换专属字段。
        const commonColumns: TableColumnProps<ShareOperationRecordDetail>[] = [
            {
                key: "index",
                label: "编号",
                prop: "index",
                render: ({ rowIndex }) => (page - 1) * 20 + rowIndex + 1
            },
            { key: "operator", label: "查看用户", prop: "operator", render: ({ row }) => valueText(row.operator) },
            { key: "mobile", label: "手机号", prop: "mobile", render: ({ row }) => valueText(row.mobile) },
            {
                key: "operateTime",
                label: "访问时间",
                prop: "operateTime",
                render: ({ row }) => dateText(row.operateTime)
            },
            {
                key: "isLogin",
                label: "用户是否登录",
                prop: "isLogin",
                sortable: "custom",
                render: ({ row }) => yesNoText(row.isLogin)
            },
            {
                key: "isRegistered",
                label: "用户之前是否注册",
                prop: "isRegistered",
                sortable: "custom",
                render: ({ row }) => yesNoText(row.isRegistered)
            },
            {
                key: "isRegisteredThisTime",
                label: "用户本次是否注册",
                prop: "isRegisteredThisTime",
                sortable: "custom",
                render: ({ row }) => yesNoText(row.isRegisteredThisTime)
            }
        ];

        if (businessType === TABS.DEMAND) {
            return [
                ...commonColumns,
                {
                    key: "isBusinessValid",
                    label: "需求状态是否有效",
                    prop: "isBusinessValid",
                    sortable: "custom" as const,
                    render: ({ row }) => yesNoText(row.isBusinessValid)
                },
                {
                    key: "isViewDetail",
                    label: "是否有查看需求",
                    prop: "isViewDetail",
                    sortable: "custom" as const,
                    render: ({ row }) => yesNoText(row.isViewDetail)
                },
                {
                    key: "jumpPath",
                    label: "需求跳转路径",
                    prop: "jumpPath",
                    render: ({ row }) => metaText(row.jumpPath, code => JUMP_PATH.getIntroByCode(code))
                },
                {
                    key: "detailViewTime",
                    label: "需求查看时间",
                    prop: "detailViewTime",
                    render: ({ row }) => dateText(row.detailViewTime)
                },
                {
                    key: "isSubmitQuotation",
                    label: "是否有提交报价",
                    prop: "isSubmitQuotation",
                    sortable: "custom" as const,
                    render: ({ row }) => yesNoText(row.isSubmitQuotation)
                },
                {
                    key: "isGenerateWaybill",
                    label: "是否生成船运单",
                    prop: "isGenerateWaybill",
                    sortable: "custom" as const,
                    render: ({ row }) => yesNoText(row.isGenerateWaybill)
                },
                {
                    key: "orderId",
                    label: "船运单号",
                    prop: "orderId",
                    render: ({ row }) =>
                        row.orderId ? (
                            <Link to={`/logistics/shippingNeed/waybillManagementInfo/${row.orderId}`}>
                                {row.orderId}
                            </Link>
                        ) : (
                            valueText(row.orderId)
                        )
                }
            ];
        }

        return [
            ...commonColumns,
            {
                key: "operatorRole",
                label: "用户身份",
                prop: "operatorRole",
                render: ({ row }) =>
                    metaText(row.operatorRole, code => TRANSPORT_ORDER_SHIP_QUERY_TYPE.getIntroByCode(code))
            },
            {
                key: "isBusinessValid",
                label: "空船状态是否有效",
                prop: "isBusinessValid",
                sortable: "custom",
                render: ({ row }) => yesNoText(row.isBusinessValid)
            },
            {
                key: "isViewDetail",
                label: "是否有查看空船",
                prop: "isViewDetail",
                sortable: "custom",
                render: ({ row }) => yesNoText(row.isViewDetail)
            },
            {
                key: "detailViewTime",
                label: "空船查看时间",
                prop: "detailViewTime",
                render: ({ row }) => dateText(row.detailViewTime)
            }
        ];
    }, [businessType, page]);

    const submitFilters = () => {
        setSubmittedFilters(filters);
        setPage(1);
    };

    const resetFilters = () => {
        setFilters({});
        setSubmittedFilters({});
        setPage(1);
    };

    const title = businessType === TABS.DEMAND ? "需求分享详情" : "空船分享详情";
    const summaryItems = businessType === TABS.DEMAND ? getDemandSummary(detail) : getEmptyShipSummary(detail);
    const aggregateItems = [
        { label: "分享用户", value: valueText(detail?.share?.operator) },
        {
            label: "用户身份",
            value: metaText(detail?.share?.operatorRole, code =>
                SHARE_OPERATION_RECORD_OPERATOR_ROLE.getIntroByCode(code)
            )
        },
        {
            label: "分享端",
            value: metaText(detail?.share?.clientType, code => SHARE_OPERATION_RECORD_CLIENT_TYPE.getIntroByCode(code))
        },
        {
            label: "分享入口",
            value: metaText(detail?.share?.operationEntry, code =>
                SHARE_OPERATION_RECORD_OPERATION_ENTRY.getIntroByCode(code)
            )
        },
        {
            label: "分享方式",
            value: metaText(detail?.share?.operatorMethod, code =>
                SHARE_OPERATION_RECORD_OPERATOR_METHOD.getIntroByCode(code)
            )
        },
        {
            label: "分享载体",
            value: metaText(detail?.share?.operatorCarrier, code =>
                SHARE_OPERATION_RECORD_OPERATOR_CARRIER.getIntroByCode(code)
            )
        },
        { label: "操作时间", value: dateText(detail?.share?.operateTime) },
        { label: "业务编号", value: valueText(detail?.share?.businessId) },
        { label: "小程序访问人数", value: valueText(detail?.miniAppVisitorCount) },
        businessType === TABS.DEMAND
            ? { label: "查看需求人数", value: valueText(detail?.viewRequirementNumber) }
            : { label: "查看空船人数", value: valueText(detail?.viewEmptyShipNumber) },
        businessType === TABS.DEMAND
            ? { label: "查看需求次数", value: valueText(detail?.viewRequirementCount) }
            : { label: "查看空船次数", value: valueText(detail?.viewEmptyShipCount) },
        ...(businessType === TABS.DEMAND ? [{ label: "报价人数", value: valueText(detail?.acceptCount) }] : [])
    ];

    // 延续 Vue ZhConfirm：导出明细时使用当前页参数，但将 size 替换为全部明细总数。
    const confirmExport = async () => {
        if (!resolvedTrackId) return;
        setExporting(true);
        try {
            await exportRows(
                {
                    page,
                    size: total,
                    trackId: resolvedTrackId,
                    businessType,
                    type: 1,
                    ...toDetailFilterQuery(submittedFilters),
                    ...(sort ? { sortKey: sort.key, sortOrder: sort.order } : {})
                },
                businessType === TABS.DEMAND ? "需求分享详情" : "空船分享详情"
            );
            setExportOpen(false);
            message.success("导出成功");
        } catch {
            setError("分享统计明细导出失败");
        } finally {
            setExporting(false);
        }
    };

    return (
        <section aria-label="share-statistical-detail-page" className="flex min-h-full flex-col gap-4">
            <ZhMultiGrid>
                <div>
                    <h2>{title}</h2>
                    {error ? <Alert role="alert" type="error" message={error} /> : null}
                    <Card title={businessType === TABS.DEMAND ? "需求信息" : "空船信息"}>
                        <ZhGrid cols={4} gap={[20, 20]}>
                            {summaryItems.map(renderDetailItem)}
                        </ZhGrid>
                    </Card>
                    <Card title="汇总信息">
                        <ZhGrid cols={4} gap={[20, 20]}>
                            {aggregateItems.map(renderDetailItem)}
                        </ZhGrid>
                    </Card>
                    <Card
                        title="明细信息"
                        extra={
                            canExport ? (
                                <Button
                                    type="primary"
                                    aria-label="导出详情"
                                    loading={exporting}
                                    onClick={() => setExportOpen(true)}
                                >
                                    导出
                                </Button>
                            ) : null
                        }
                    >
                        <ZhTable<ShareOperationRecordDetail>
                            rowKey={row => `${row.trackId}-${row.eventTime}`}
                            data={rows}
                            columns={columns}
                            emptyText="暂无数据"
                            pagination={false}
                            onSortChange={({ prop, order }) => {
                                if (!prop) return;
                                setSort(
                                    order ? { key: prop, order: order === "ascending" ? "asc" : "desc" } : undefined
                                );
                                setPage(1);
                            }}
                            isShowResetBtn={false}
                            isShowSearchBtn={false}
                            tableHeader={
                                <div className="admin-table-composed-header">
                                    <Space wrap>
                                        <ZhDateRangePicker
                                            aria-label="访问时间"
                                            value={filters.operateTime}
                                            valueFormat="YYYY-MM-DD"
                                            format="YYYY-MM-DD"
                                            startPlaceholder="访问开始时间"
                                            endPlaceholder="访问结束时间"
                                            onChange={(_value, formattedValue) =>
                                                setFilters(current => ({ ...current, operateTime: formattedValue }))
                                            }
                                        />
                                        {businessType === TABS.DEMAND ? (
                                            <ZhSelect
                                                aria-label="需求跳转路径"
                                                allowClear
                                                placeholder="请选择需求跳转路径"
                                                value={filters.jumpPath}
                                                options={selectOptions(JUMP_PATH.options)}
                                                onChange={value =>
                                                    setFilters(current => ({
                                                        ...current,
                                                        jumpPath: value as number | undefined
                                                    }))
                                                }
                                            />
                                        ) : (
                                            <ZhSelect
                                                aria-label="用户身份"
                                                allowClear
                                                placeholder="请选择用户身份"
                                                value={filters.operatorRole}
                                                options={selectOptions(TRANSPORT_ORDER_SHIP_QUERY_TYPE.options)}
                                                onChange={value =>
                                                    setFilters(current => ({
                                                        ...current,
                                                        operatorRole: value as number | undefined
                                                    }))
                                                }
                                            />
                                        )}
                                        <Button type="primary" aria-label="查询明细" onClick={submitFilters}>
                                            查询
                                        </Button>
                                        <Button aria-label="重置明细筛选" onClick={resetFilters}>
                                            重置
                                        </Button>
                                    </Space>

                                    <div className="text-sm text-black/40">
                                        {businessType === TABS.DEMAND
                                            ? "注：用户是否登录、用户之前是否注册、需求状态是否有效、是否有查看需求都为用户通过分享进入小程序时的状态记录，后续不会更新"
                                            : "注：用户是否登录、用户身份、用户之前是否注册、空船状态是否有效、是否有查看空船都为用户通过分享进入小程序时的状态记录，后续不会更新"}
                                    </div>
                                </div>
                            }
                            append={
                                <div className="admin-table-composed-pagination">
                                    <ZhPagination
                                        current={page}
                                        pageSize={20}
                                        total={total}
                                        hideOnSinglePage
                                        onChange={setPage}
                                    />
                                </div>
                            }
                        />
                    </Card>
                </div>
            </ZhMultiGrid>
            <Space>
                <Button onClick={() => navigate(-1)}>返回</Button>
            </Space>
            <ZhDialog
                open={exportOpen}
                title="导出"
                width={400}
                closeOnClickModal={false}
                onCancel={() => setExportOpen(false)}
                footer={
                    <Space>
                        <Button onClick={() => setExportOpen(false)}>取消</Button>
                        <Button type="primary" loading={exporting} onClick={() => void confirmExport()}>
                            确认
                        </Button>
                    </Space>
                }
            >
                是否导出当前数据？
            </ZhDialog>
        </section>
    );
}

export default ShareStatisticalDetailPage;
